import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

const services = [
  {
    icon: "📄",
    title: "Structural Blueprints",
    desc: "Comprehensive SRS, High-Level Architecture, and Technical Specifications for every build.",
    features: [
      "Software Requirements Specification (SRS)",
      "UML Systems (Class, Sequence, Activity)",
      "Database Schema & ERD Architecture",
      "Network & Security Blueprints"
    ]
  },
  {
    icon: "📖",
    title: "Intelligence Manuscripts",
    desc: "Rigorous research reports and academic literature synthesis for the global elite.",
    features: [
      "Exhaustive Literature Reviews",
      "Methodology Engineering",
      "Quantitative Data Analytics",
      "Strategic Conclusion Synthesis"
    ]
  },
  {
    icon: "📊",
    title: "Executive Media",
    desc: "Premium pitch decks and technical defense presentations for mission-critical meetings.",
    features: [
      "High-Impact Executive Overviews",
      "System Flow Visualizations",
      "Technical Deep-Dive Slides",
      "Animated Process Flows"
    ]
  },
  {
    icon: "📉",
    title: "Authenticity Guard",
    desc: "Integrity-verified documentation with guaranteed <4% similarity index benchmark.",
    features: [
      "Zero-AI Detection Verification",
      "Authentic Citation Networks",
      "Turnitin Integrity Reports",
      "Standardized Referencing"
    ]
  }
];

const testimonials = [
  {
    name: "Dr. Sarah J.",
    org: "Intelligence Specialist",
    text: "The architectural depth of the documentation provided was beyond industrial standards. precision incarnate.",
    rate: 5
  },
  {
    name: "Michael R.",
    org: "Systems Engineer",
    text: "Seamless communication and high-fidelity reports. An essential partner for complex architectural builds.",
    rate: 5
  }
];

export default function Documentation() {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 left-0 w-[900px] h-[900px] bg-blue-500/5 blur-[140px] rounded-full -ml-40 -mt-40 animate-blob" />
      <div className="fixed bottom-0 right-0 w-[700px] h-[700px] bg-[#020617]0/5 blur-[120px] rounded-full -mr-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-64 pb-32 px-6 flex flex-col items-center text-center overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-16"
        >
          <div className="absolute -inset-14 bg-blue-500/10 blur-[130px] rounded-full pointer-events-none" />
          <h1 className="text-5xl md:text-8xl lg:text-[10.5rem] font-black text-white mb-10 tracking-[0.02em] leading-[0.85] relative z-10 uppercase font-inter px-4">
            Technical <br /><span className="text-gradient from-blue-600 via-slate-600 to-indigo-600">Intelligence.</span>
          </h1>
        </motion.div>

        <p className="text-lg md:text-2xl lg:text-3xl text-slate-400 max-w-4xl mb-20 leading-relaxed relative z-10 font-medium lowercase tracking-tight px-6">
          we transform complex engineering logic into high-fidelity technical documentation.
          precision-engineered manuscripts for global enterprise standards and defense protocols.
        </p>

        <div className="flex gap-10 relative z-10">
          <a
            href="https://wa.me/94723954658"
            target="_blank"
            rel="noopener noreferrer"
            className="px-16 py-7 bg-slate-900 text-white font-black rounded-[2.5rem] shadow-2xl hover:bg-black transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-[11px] flex items-center gap-3"
          >
            REQUEST MANUSCRIPT
            <span className="text-xl transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-40 px-6 relative z-10 bg-[#f1f5f9]/40 border-y border-white/5 backdrop-blur-sm">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className="group glass-card p-10 md:p-16 rounded-[3rem] md:rounded-[4.5rem] border border-white/80 hover:bg-slate-900/40/95 transition-all duration-700 hover:shadow-2xl hover:translate-y-[-10px] glow-border relative overflow-hidden cursor-default"
              >
                <div className="absolute top-16 right-16 text-[8rem] opacity-[0.03] group-hover:opacity-10 group-hover:rotate-12 transition-all duration-1000 pointer-events-none">{service.icon}</div>
                <div className="w-24 h-24 bg-slate-900/40 text-5xl mb-12 flex items-center justify-center rounded-3xl border border-white/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm">
                  {service.icon}
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-white mb-8 tracking-tighter uppercase leading-none">{service.title}</h3>
                <p className="text-slate-400 font-medium text-base md:text-lg mb-14 leading-relaxed max-w-sm lowercase tracking-tight">{service.desc}</p>

                <div className="space-y-6 pt-10 border-t border-white/5">
                  {service.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-6 group/item">
                      <div className="w-2.5 h-2.5 bg-blue-100 border border-blue-600 rounded-full transition-all group-hover/item:bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.2)]" />
                      <span className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] leading-none">{f}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Stats */}
      <section className="py-40 px-6 border-y border-white/5 bg-slate-900/40 relative z-10 overflow-hidden">
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-3 gap-24 text-center">
            {[
              { label: "Manuscript Originality", val: ">96%" },
              { label: "Regulatory Compliance", val: "100%" },
              { label: "Engineering Precision", val: "High-Fi" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -8 }}
              >
                <div className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tighter uppercase leading-none">{stat.val}</div>
                <div className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] md:tracking-[0.5em] px-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-40 px-6 relative z-10 bg-[#f8fafc]">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <h2 className="text-[11px] font-black text-slate-400 mb-28 text-center uppercase tracking-[0.6em]">Verification Authority</h2>
          <div className="space-y-12">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="glass-card p-16 rounded-[4rem] border border-white/80 relative hover:shadow-2xl hover:bg-slate-900/40/95 transition-all duration-700"
              >
                <p className="text-xl md:text-2xl text-slate-100 font-medium mb-12 leading-relaxed italic lowercase tracking-tight">"{t.text}"</p>
                <div className="flex items-center gap-8 pt-12 border-t border-white/5">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm">
                    <span className="text-blue-600 font-black text-xs uppercase tracking-widest">Auth</span>
                  </div>
                  <div>
                    <div className="text-lg font-black text-white uppercase tracking-tighter leading-none">{t.name}</div>
                    <div className="text-[11px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">{t.org}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-48 px-6 relative z-10 bg-slate-900/40">
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 md:p-16 rounded-[3.5rem] text-center border border-white/10 shadow-[0_60px_120px_rgba(0,0,0,0.5)] relative overflow-hidden"
          >
            <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-blue-500/10 blur-[130px] rounded-full" />
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tighter uppercase relative z-10 leading-none px-4">Authorize <br /><span className="text-gradient">Immensity.</span></h2>
            <div className="flex justify-center gap-10 relative z-10">
              <a
                href="https://wa.me/94723954658"
                className="px-16 py-7 bg-blue-600 text-white font-black rounded-[2.5rem] hover:bg-blue-700 transition-all shadow-[0_20px_40px_rgba(37,99,235,0.3)] hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
              >
                REQUEST AUDIT
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-24 px-6 bg-[#020617] border-t border-white/5 text-center">
        <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.5em]">© 2024 LAB Developers — Intelligence Unit.</p>
      </footer>
    </div>
  );
}
